import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from 'src/utils/token-service';
import './ClusterManager.css';

interface Tag {
    tagname: string;
    tagID: string;
    isTagVerified: boolean
}
interface Cluster {
    cluster_id: any;
    cluster_name: string;
    tags: Tag[];
    cluster_created_at: string;
    cluster_updated_at: string;
    is_cluster_verified: boolean;
    is_cluster_active: boolean;
    is_suggestion_cluster: boolean;
}

const parentIntentIds: { [key: string]: string } = {
    'Billing': 'a058f5ec-816d-406f-ab9b-28cad425ea61',
    'Service Disruptions': '4d4f3359-dbad-4401-a570-52f21d797a21',
    'Payments': '70a605f5-e407-4069-9ca0-48e36bbedcb6',
    'Account Management': '5cc25966-976b-4472-a1cd-3194d06fa10b',
    'Service Management': '424485f3-ce69-471f-82c8-252432b7173b'
};

const ClusterManager = () => {
    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedClusters, setSelectedClusters] = useState<Cluster[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [showNonVerified, setShowNonVerified] = useState<boolean>(false);
    const [editingIndex, setEditingIndex] = useState<string | null>(null);
    const [editedName, setEditedName] = useState<string>('');

    const [mergeModalOpen, setMergeModalOpen] = useState<boolean>(false);
    const [newClusterName, setNewClusterName] = useState<string>('');
    const [useDropdown, setUseDropdown] = useState<boolean>(true);
    const [expandedClusters, setExpandedClusters] = useState<{ [key: string]: boolean }>({});

    const [errorMessages, setErrorMessages] = useState<{ [key: string]: string | null }>({});


    const clustersPerPage = 12;

    const [selectedIntent, setSelectedIntent] = useState<'customer_intent' | 'problem_identified' | 'root_cause'>('customer_intent');
    const [selectedParentIntent, setSelectedParentIntent] = useState<string>('Billing');
    const [draggedTag, setDraggedTag] = useState<{ tag: Tag, fromClusterId: string } | null>(null);
    const [movedTags, setMovedTags] = useState<{ cluster_id: string; tagID: string[] }[]>([]);
    const [toggledTagIds, setToggledTagIds] = useState<string[]>([]);

    const handleDragStart = (tag: Tag, fromClusterId: string) => {
        setDraggedTag({ tag, fromClusterId });
    };

    const handleDrop = (toClusterId: string) => {
        console.log("Target", toClusterId)
        if (!draggedTag) return;
        const { tag, fromClusterId } = draggedTag;
        console.log("draggedTag", tag, fromClusterId)
        if (fromClusterId === toClusterId) return;

        setClusters(prevClusters => prevClusters.map(cluster => {
            if (cluster.cluster_id === fromClusterId) {
                return { ...cluster, tags: cluster.tags.filter(t => t.tagID !== tag.tagID) };
            }
            if (cluster.cluster_id === toClusterId) {
                return { ...cluster, tags: [...cluster.tags, tag] };
            }
            return cluster;
        }));

        setMovedTags((prev) => {
            let updatedTags = prev.map((entry) => ({
                ...entry,
                tagID: entry.tagID.filter((id) => id !== draggedTag.tag.tagID),
            })).filter((entry) => entry.tagID.length > 0);

            const isTagInOriginalCluster = clusters.some(
                (cluster) =>
                    cluster.cluster_id === toClusterId &&
                    cluster.tags.some((tag) => tag.tagID === draggedTag.tag.tagID)
            );

            if (isTagInOriginalCluster) {
                return updatedTags;
            }

            const existingEntry = updatedTags.find((entry) => entry.cluster_id === toClusterId);
            if (existingEntry) {
                return updatedTags.map((entry) =>
                    entry.cluster_id === toClusterId
                        ? { ...entry, tagID: [...new Set([...entry.tagID, draggedTag.tag.tagID])] }
                        : entry
                );
            } else {
                return [...updatedTags, { cluster_id: toClusterId, tagID: [draggedTag.tag.tagID] }];
            }
        });

        setDraggedTag(null);
    };

    const handleApply = async () => {
        console.log("movedTags:", movedTags);
        console.log("Toggle tag list: ", toggledTagIds);
        let updateOccurred = false;
        if (toggledTagIds.length > 0) {
            const accessToken = await getAccessToken();
            const payload = {
                parent_id: parentIntentIds[selectedParentIntent],
                tag_ids: toggledTagIds,
                mapping_type: selectedIntent,
            };

            try {
                const response = await fetch("https://api-dv.brightspeed.com/genai-oa-service/update_tag_status", {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                alert("Tag verifications updated successfully!");
                updateOccurred = true;
            } catch (error) {
                console.error("Error toggling tags: ", error);
                alert("Failed to update tag verifications");
            }

        }

        // API call to drag and drop tags from one cluster to another
        if (movedTags.length > 0) {
            const accessToken = await getAccessToken();
            const payload = {
                parent_id: parentIntentIds[selectedParentIntent],
                mapping_type: selectedIntent,
                mappings: movedTags,
            };

            try {
                const response = await fetch("https://api-dv.brightspeed.com/genai-oa-service/update_mapping", {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                console.log("Save successful:", data);
                alert("Tags mapped successfully!");
                updateOccurred = true;
            } catch (error) {
                console.error("Error saving clusters:", error);
                alert("Failed to save clusters. Please try again.");
            }

        }
        setMovedTags([]);
        setToggledTagIds([]);
        if (updateOccurred) {
            await fetchRequests();
        }
    };

    const handleCancel = () => {
        setMovedTags([]);
        setToggledTagIds([]);
        fetchRequests();
    };

    const startEditing = (index: string, currentName: string) => {
        setEditingIndex(index);
        setEditedName(currentName);
    };

    const handleRename = async (index: string) => {
        const cluster = clusters.find((c) => c.cluster_id === index);
        if (!cluster) return;

        // Skip API call if name hasn't changed
        if (editedName.trim() === cluster.cluster_name) {
            setEditingIndex(null);
            return;
        }

        if (editedName.trim()) {
            const accessToken = await getAccessToken();
            const parentId = parentIntentIds[selectedParentIntent];
            const mappingType = selectedIntent;

            try {
                const response = await fetch('https://api-dv.brightspeed.com/genai-oa-service/update_cluster_name', {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        parent_id: parentId,
                        cluster_id: index,
                        updated_cluster_name: editedName.trim(),
                        mapping_type: mappingType,
                    }),
                });

                const responseData = await response.json();
                if (!response.ok) {
                    if (responseData.detail && responseData.detail.includes('Cluster Name Already Exists')) {
                        setErrorMessages((prevErrors) => ({
                            ...prevErrors,
                            [index]: 'Cluster Name Already Exists. Please provide a different name!',
                        }));
                        return; // Stop further execution
                    } else {
                        throw new Error('Failed to update cluster name');
                    }
                } else {
                    const updatedClusters = [...clusters];
                    const filtercluster = updatedClusters.find((c) => c.cluster_id === index);

                    if (filtercluster) {
                        filtercluster.cluster_name = editedName.trim();
                    }

                    setClusters(updatedClusters);
                    setErrorMessages((prevErrors) => ({
                        ...prevErrors,
                        [index]: null, // Clear error if successful
                    }));
                    alert('Cluster updated successfully!');
                }
            } catch (error) {
                console.error('Error updating cluster name:', error);
                setErrorMessages((prevErrors) => ({
                    ...prevErrors,
                    [index]: 'Failed to update cluster name. Please try again.',
                }));
            }
        }
        setEditingIndex(null);
    };


    const cancelEditing = () => {
        setEditingIndex(null);
        setEditedName('');
    };

    const fetchRequests = async () => {
        setLoading(true);
        const accessToken = await getAccessToken();
        const mappingType = selectedIntent;
        const parentId = parentIntentIds[selectedParentIntent];

        let apiUrl = `https://api-dv.brightspeed.com/genai-oa-service/view_all_mapping?parent_id=${parentId}&mapping_type=${mappingType}`;

        if (showNonVerified) apiUrl += `&is_cluster_verified=false`;

        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const data = await response.json();
            const parsedData: Cluster[] = JSON.parse(data);
            const sortedData = parsedData.sort((a, b) => a.cluster_name.localeCompare(b.cluster_name));
            setClusters(sortedData);
        } catch (error) {
            console.error('Error fetching clusters:', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchRequests();
    }, [showNonVerified, selectedIntent, selectedParentIntent]);

    const navigate = useNavigate();
    const handleCompare = () => {
        const mappingType = selectedIntent;
        const parentId = parentIntentIds[selectedParentIntent];
        navigate('/opportunity-analytics/compare', { state: { selectedClusters, parentId, mappingType } });
    };

    const toggleClusterSelection = (id: Cluster) => {
        setSelectedClusters((prev) =>
            prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
        );
    };

    const filteredClusters = clusters.filter((c) =>
        c.cluster_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalPages = Math.ceil(filteredClusters.length / clustersPerPage);
    const displayedClusters = filteredClusters.slice(
        (currentPage - 1) * clustersPerPage,
        currentPage * clustersPerPage
    );

    const toggleShowMore = (clusterId: string) => {
        setExpandedClusters((prev) => ({
            ...prev,
            [clusterId]: !prev[clusterId],
        }));
    };

    const handleMerge = () => setMergeModalOpen(true);

    const confirmMerge = async () => {
        if (!newClusterName.trim()) {
            alert('Please provide a new cluster name.');
            return;
        }

        const isNewCluster = !selectedClusters.some(cluster => cluster.cluster_name === newClusterName);
        const oldClusterIds = selectedClusters.map(cluster => cluster.cluster_id);
        const parentId = parentIntentIds[selectedParentIntent];
        const mappingType = selectedIntent;
        const accessToken = await getAccessToken();
        let updateOccurred = false;

        const payload = {
            parent_id: parentId,
            target_cluster_name: newClusterName,
            mapping_type: mappingType,
            old_cluster_ids: oldClusterIds,
            is_new_cluster: isNewCluster
        };

        try {
            const response = await fetch('https://api-dv.brightspeed.com/genai-oa-service/merge_clusters', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to merge clusters');
            }

            alert('Clusters merged successfully!');
            setMergeModalOpen(false);
            setNewClusterName('');
            setSelectedClusters([]);
            updateOccurred = true;
        } catch (error) {
            console.error('Error merging clusters:', error);
            alert('Failed to merge clusters. Please try again.');
        }

        if (updateOccurred) {
            await fetchRequests();
        }

    };

    const toggleTagVerification = (clusterId: string, tagId: string) => {
        // Update tag verification status
        setClusters(prevClusters =>
            prevClusters.map(cluster =>
                cluster.cluster_id === clusterId
                    ? {
                        ...cluster,
                        tags: cluster.tags.map(tag =>
                            tag.tagID === tagId
                                ? { ...tag, isTagVerified: !tag.isTagVerified }
                                : tag
                        )
                    }
                    : cluster
            )
        );

        // Update toggled tag list (avoid duplicates)
        setToggledTagIds(prev => (
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId) // remove if toggled again
                : [...prev, tagId]
        ));
    };



    return (
        <div>
            <h2 style={{ fontWeight: 'bold', marginBottom: '20px', textAlign: 'left', textTransform: 'uppercase' }}>Opportunity Data Analysis</h2>
            {loading ? (
                <div className="spinner"></div>
            ) : (
                <div>

                    <div className="search-filter-container">
                        <input
                            type="text"
                            placeholder="Search Cluster Names"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-bar reduced-width"
                        // style={{ flex: '1', minWidth: '200px' }}
                        />

                        <div className="intent-dropdown" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label htmlFor="intent-select">Tag:</label>
                            <select
                                id="intent-select"
                                value={selectedIntent}
                                onChange={(e) => setSelectedIntent(e.target.value as 'customer_intent' | 'problem_identified' | 'root_cause')}
                                style={{ paddingRight: '24px' }}
                            >
                                <option value="customer_intent">Customer Intent</option>
                                <option value="problem_identified">Problem Identified</option>
                                <option value="root_cause">Root Cause</option>
                            </select>

                            <label htmlFor="parent-intent-select">Parent Intent:</label>
                            <select
                                id="parent-intent-select"
                                value={selectedParentIntent}
                                onChange={(e) => setSelectedParentIntent(e.target.value)}
                                style={{ paddingRight: '24px' }}
                            >
                                <option value="Billing">Billing</option>
                                <option value="Service Disruptions">Service Disruptions</option>
                                <option value="Payments">Payments</option>
                                <option value="Account Management">Account Management</option>
                                <option value="Service Management">Service Management</option>
                            </select>
                        </div>

                        <div className="toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={showNonVerified}
                                    onChange={() => setShowNonVerified(!showNonVerified)}
                                />
                                <span className="slider"></span>
                            </label>
                            <span>{showNonVerified ? 'Non-Verified Clusters' : 'All Clusters'}</span>
                        </div>
                    </div>

                    {/* Table or No Data Message */}
                    {clusters.length === 0 ? (
                        <div className="alert">No data available</div>
                    ) : (
                        <>
                            <table>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Cluster Name</th>
                                        <th>Verbatim</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedClusters.map((cluster) => (
                                        <tr key={cluster.cluster_id} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop(cluster.cluster_id)}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedClusters.includes(cluster)}
                                                    onChange={() => toggleClusterSelection(cluster)}
                                                />
                                            </td>
                                            <td className="cluster-name-cell">
                                                <div className="cluster-name">
                                                    {editingIndex === cluster.cluster_id ? (
                                                        <div>
                                                            <input
                                                                type="text"
                                                                value={editedName}
                                                                onChange={(e) => {
                                                                    setEditedName(e.target.value)
                                                                    setErrorMessages((prevErrors) => ({
                                                                        ...prevErrors,
                                                                        [cluster.cluster_id]: null, // Clear error when user types
                                                                    }));
                                                                }}
                                                                className="edit-input"
                                                            />
                                                            {errorMessages[cluster.cluster_id] && (
                                                                <p className="error-message">{errorMessages[cluster.cluster_id]}</p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span>{cluster.cluster_name}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {cluster.tags.length > 5 ? (
                                                    <>
                                                        {expandedClusters[cluster.cluster_id] ? (
                                                            <div className="tags-container">
                                                                {cluster.tags.map(tag => (
                                                                    <span
                                                                        key={tag.tagID}
                                                                        draggable
                                                                        onDragStart={() => handleDragStart(tag, cluster.cluster_id)}
                                                                        // className="tag"
                                                                        onDoubleClick={() => toggleTagVerification(cluster.cluster_id, tag.tagID)}
                                                                        className={`tag ${tag.isTagVerified ? "lightblue-bg" : "lightcoral-bg"}`}
                                                                    // style={{ backgroundColor: tag.isTagVerified ? 'lightblue':'lightcoral'}}
                                                                    >
                                                                        {tag.tagname}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="tags-container">
                                                                {cluster.tags.slice(0, 5).map(tag => (
                                                                    <span
                                                                        key={tag.tagID}
                                                                        draggable
                                                                        onDragStart={() => handleDragStart(tag, cluster.cluster_id)}
                                                                        //className="tag"
                                                                        onDoubleClick={() => toggleTagVerification(cluster.cluster_id, tag.tagID)}
                                                                        className={`tag ${tag.isTagVerified ? "lightblue-bg" : "lightcoral-bg"}`}
                                                                    >
                                                                        {tag.tagname}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button
                                                            className="show-more-btn"
                                                            onClick={() => toggleShowMore(cluster.cluster_id)}
                                                        >
                                                            {expandedClusters[cluster.cluster_id] ? 'Show Less' : 'Show More'}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="tags-container">
                                                        {cluster.tags.map(tag => (
                                                            <span
                                                                key={tag.tagID}
                                                                draggable
                                                                onDragStart={() => handleDragStart(tag, cluster.cluster_id)}
                                                                // className="tag"
                                                                onDoubleClick={() => toggleTagVerification(cluster.cluster_id, tag.tagID)}
                                                                className={`tag ${tag.isTagVerified ? "lightblue-bg" : "lightcoral-bg"}`}
                                                            >
                                                                {tag.tagname}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {editingIndex === cluster.cluster_id ? (
                                                    <div className="action-buttons">
                                                        <button className="save-btn" onClick={() => handleRename(cluster.cluster_id)}>Save</button>
                                                        <button className="cancel-btn" onClick={cancelEditing}>Cancel</button>
                                                    </div>
                                                ) : (
                                                    <button className="edit-icon" onClick={() => startEditing(cluster.cluster_id, cluster.cluster_name)}>
                                                        ✏️
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="pagination">
                                <button className='pagination-btn' onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                                    Prev
                                </button>
                                <span>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button className='pagination-btn' onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                                    Next
                                </button>
                            </div>

                            {selectedClusters.length >= 2 && (
                                <div className="actions">
                                    <button onClick={handleCompare}>Compare</button>
                                    <button onClick={handleMerge}>Merge</button>
                                    <button onClick={() => setSelectedClusters([])}>Cancel</button>
                                </div>
                            )}

                            {(movedTags.length > 0 || toggledTagIds.length > 0) && (
                                <div className="actions">
                                    <button onClick={handleApply}>Apply</button>
                                    <button onClick={handleCancel}>Cancel</button>
                                </div>
                            )}

                            {mergeModalOpen && (
                                <div className="modal">
                                    <h3 style={{ fontWeight: 'bold' }}>Merge Clusters</h3>
                                    <div>
                                        <label style={{ marginRight: '25px' }}>
                                            <input type="radio" checked={useDropdown} onChange={() => setUseDropdown(true)} /> Select from existing cluster name
                                        </label>
                                        <label style={{ marginRight: '25px' }}>
                                            <input type="radio" checked={!useDropdown} onChange={() => setUseDropdown(false)} /> Enter new cluster name
                                        </label>
                                    </div>
                                    {useDropdown ? (
                                        <select style={{ margin: '10px' }} onChange={(e) => setNewClusterName(e.target.value)}>
                                            <option value="">Select Cluster Name</option>
                                            {selectedClusters.map(cluster => (
                                                <option key={cluster.cluster_id} value={cluster.cluster_name}>{cluster.cluster_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input type="text" placeholder="Enter new cluster name" value={newClusterName} onChange={(e) => setNewClusterName(e.target.value)} />
                                    )}
                                    <button onClick={confirmMerge}>Confirm Merge</button>
                                    <button onClick={() => setMergeModalOpen(false)}>Cancel</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClusterManager;
